import { MarkerEditorProperties, TextMarkerEditor } from "@markerjs/markerjs3";
import LSTextMarker from "./LSTextMarker";
import { wrapTextByWidth } from "./MarkerHelpers";
import { keepTextMarkerWithinTarget } from "./keepTextMarkerWithinTarget";

export default class LSTextMarkerEditor extends TextMarkerEditor {
	private _textWidthContext!: CanvasRenderingContext2D;
	private _maximumMarkerBottom?: number;

	constructor(properties: MarkerEditorProperties<LSTextMarker>) {
		super(properties);

		const canvas = document.createElement("canvas");
		const context = canvas.getContext("2d");

		if (context) {
			this._textWidthContext = context;
		}

		const newWrapText = () => {
			const textEditor = this.textBlockEditorContainer.children?.[0] as HTMLTextAreaElement;

			if (textEditor) {
				const wrappedText = wrapTextByWidth(
					this.marker.text,
					this.marker.fontSize.value,
					this.marker.fontFamily,
					textEditor,
					this._textWidthContext
				);

				return wrappedText;
			}

			return this.marker.text;
		};

		(this.marker.textBlock as any).wrapText = newWrapText;

		this.marker.textBlock.labelBackground.style.background = "#ff0000";
		this.marker.textBlock.labelBackground.setAttribute("fill", "#ff0000");
		this.marker.textBlock.wordWrap = true;

		/*
            The default behavior of markerjs V3 is weird for the content editable div.
            The event listeners they have set up cause some weird behavior on mobile as well as pasting issues
            on mobile and desktop. Overriding the div element with mostly the same properties/styling as in
            the source code but removing some of the event listeners they have attached.
            Reference: https://github.com/ailon/markerjs3/blob/eb088a37c7d206e9548c4b1bc0196856d5bd10b9/src/editor/TextBlockEditor.ts#L174
        */

		const editor = (this.textBlockEditor as any).textEditor as HTMLTextAreaElement;
		const clone = editor.cloneNode(false) as HTMLTextAreaElement;

		editor.style.textAlign = "center";

		clone.style.alignContent = "center";
		clone.style.justifyContent = "center";
		clone.style.resize = "none";
		clone.style.textAlign = "center";
		clone.style.fontFamily = this.fontFamily;
		clone.style.fontSize = `${this.marker.fontSize?.value}${this.marker.fontSize?.units}`;
		clone.style.lineHeight = "1em";
		clone.style.height = `${this.marker.height}px`;
		clone.style.borderRadius = "8px";
		clone.style.border = "none";
		clone.style.padding = "0";
		clone.style.outline = "none";
		clone.style.overflow = "hidden";

		if (this.marker.text !== "") {
			clone.value = this.marker.text;
		} else {
			clone.value = "Text";
		}

		clone.addEventListener("keydown", (ev) => {
			if (ev.key === "Escape") {
				ev.preventDefault();
				clone.blur();
			}
		});

		clone.addEventListener("keyup", (ev) => {
			ev.stopPropagation();
			this.textBlockEditor.text = clone.value;
			if (this.textBlockEditor.onTextChanged !== undefined) {
				this.textBlockEditor.onTextChanged(clone.value);
			}
		});

		clone.addEventListener("blur", () => {
			this.textBlockEditor.text = clone.value;
			if (this.textBlockEditor.onTextChanged !== undefined) {
				this.textBlockEditor.onTextChanged(clone.value);
			}
			if (this.textBlockEditor.onBlur !== undefined) {
				this.textBlockEditor.onBlur();
			}
		});

		requestAnimationFrame(() => {
			clone.focus();
			clone.select();
		});

		(this.textBlockEditor as any).textEditor = clone;
		(this.textBlockEditor as any).isSetupCompleted = true;
	}

	deselect(): void {
		if (this.marker.text === "\n" || this.marker.text === "") {
			this.marker.text = "Text";

			const textEditor = this.textBlockEditorContainer.children?.[0];

			if (textEditor instanceof HTMLTextAreaElement) {
				textEditor.value = "Text";
			}
		}

		super.deselect();
	}

	setMaximumMarkerBottom(maximumMarkerBottom: number): void {
		this._maximumMarkerBottom = maximumMarkerBottom;
		this.setSize();
	}

	/**
	 * Override the default logic and force the size of the text editor to always be 300px
	 */
	protected setSize(): void {
		super.setSize();
		keepTextMarkerWithinTarget(this.marker, this._maximumMarkerBottom);
		this.textBlockEditorContainer.style.display = "flex";
		this.textBlockEditorContainer.style.transform = `translate(${this.marker.left}px, ${this.marker.top}px)`;
		this.textBlockEditorContainer.style.width = `300px`;
		this.textBlockEditorContainer.setAttribute("width", `${300}`);
		this.textBlockEditorContainer.style.height = `${this.marker.height}px`;
		this.textBlockEditorContainer.setAttribute("height", `${this.marker.height}`);
		this.textBlockEditor.width = 300;
		this.textBlockEditor.height = this.marker.height - 4;
	}
}
